import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = { name: '', description: '', price: 0, category: '', is_online: true, is_active: true, sort_order: 0, tax_rate: 2.6 };

const ShopProductsTab = ({ products, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);

  const handleOpen = (product = null) => {
    if (product) {
      setEditing(product);
      setFormData({ name: product.name || '', description: product.description || '', price: product.price || 0, category: product.category || '', is_online: product.is_online !== false, is_active: product.is_active !== false, sort_order: product.sort_order || 0, tax_rate: product.tax_rate != null ? product.tax_rate : 2.6 });
    } else {
      setEditing(null);
      setFormData(INITIAL_FORM);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      if (editing) {
        await axios.put(`${API}/products/${editing.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Produkt aktualisiert');
      } else {
        await axios.post(`${API}/products`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Produkt erstellt');
      }
      setDialogOpen(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Dieses Produkt wirklich löschen?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Produkt gelöscht');
      onRefresh();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle data-testid="shop-products-title">Shop-Produkte ({products.filter(p => p.is_active).length})</CardTitle>
            <CardDescription>Physische Produkte (Supplements, Handtücher, Zubehör etc.) für den Kundenportal-Shop</CardDescription>
          </div>
          <Button onClick={() => handleOpen(null)} data-testid="add-shop-product-btn">
            <Plus className="w-4 h-4 mr-2" />
            Neues Produkt
          </Button>
        </CardHeader>
        <CardContent>
          {products.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Noch keine Produkte angelegt</p>
              <p className="text-sm mt-1">Erstellen Sie Ihr erstes Produkt für den Shop</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Kategorie</TableHead>
                  <TableHead>Preis</TableHead>
                  <TableHead>MWST</TableHead>
                  <TableHead>Online</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.id} data-testid={`shop-product-row-${product.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{product.name}</p>
                        {product.description && <p className="text-xs text-gray-500 truncate max-w-xs">{product.description}</p>}
                      </div>
                    </TableCell>
                    <TableCell>{product.category ? <Badge variant="outline">{product.category}</Badge> : <span className="text-gray-400">-</span>}</TableCell>
                    <TableCell className="font-semibold">CHF {product.price?.toFixed(2)}</TableCell>
                    <TableCell className="text-sm text-gray-600" data-testid={`shop-product-tax-${product.id}`}>{(product.tax_rate != null ? product.tax_rate : 2.6)}%</TableCell>
                    <TableCell>
                      <Badge className={product.is_online ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {product.is_online ? 'Online' : 'Nur Vor-Ort'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={product.is_active ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}>
                        {product.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleOpen(product)} data-testid={`edit-shop-product-${product.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(product.id)} className="text-red-600 hover:bg-red-50" data-testid={`delete-shop-product-${product.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Produkt bearbeiten' : 'Neues Produkt erstellen'}</DialogTitle>
            <DialogDescription>{editing ? 'Bearbeiten Sie die Shop-Produktdetails' : 'Fügen Sie ein neues Shop-Produkt hinzu'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="shop-product-name">Produktname *</Label>
              <Input id="shop-product-name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="z.B. Protein-Shake, Handtuch" data-testid="shop-product-name-input" />
            </div>
            <div>
              <Label htmlFor="shop-product-description">Beschreibung</Label>
              <Textarea id="shop-product-description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Kurze Beschreibung des Produkts" rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="shop-product-price">Preis (CHF) *</Label>
                <Input id="shop-product-price" type="number" step="0.01" min="0" value={formData.price} onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0})} data-testid="shop-product-price-input" />
              </div>
              <div>
                <Label htmlFor="shop-product-category">Kategorie</Label>
                <Select value={formData.category || 'none'} onValueChange={(v) => setFormData({...formData, category: v === 'none' ? '' : v})}>
                  <SelectTrigger id="shop-product-category"><SelectValue placeholder="Kategorie wählen" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Keine Kategorie</SelectItem>
                    <SelectItem value="Supplements">Supplements</SelectItem>
                    <SelectItem value="Zubehör">Zubehör</SelectItem>
                    <SelectItem value="Kleidung">Kleidung</SelectItem>
                    <SelectItem value="Getränke">Getränke</SelectItem>
                    <SelectItem value="Sonstiges">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="shop-product-tax">MWST-Satz</Label>
                <Select value={String(formData.tax_rate ?? 2.6)} onValueChange={(v) => setFormData({...formData, tax_rate: parseFloat(v)})}>
                  <SelectTrigger id="shop-product-tax" data-testid="shop-product-tax-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="8.1">8.1% (Normalsatz)</SelectItem>
                    <SelectItem value="2.6">2.6% (reduziert)</SelectItem>
                    <SelectItem value="3.8">3.8% (Sondersatz)</SelectItem>
                    <SelectItem value="0">0% (befreit)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Online im Portal anzeigen</p>
                  <p className="text-xs text-gray-500">Wenn aktiviert, können Kunden das Produkt im Portal sehen und kaufen</p>
                </div>
                <input type="checkbox" checked={formData.is_online} onChange={(e) => setFormData({...formData, is_online: e.target.checked})} className="w-5 h-5" />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Produkt aktiv</p>
                  <p className="text-xs text-gray-500">Inaktive Produkte werden nicht mehr angezeigt</p>
                </div>
                <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="w-5 h-5" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} data-testid="save-shop-product-btn">{editing ? 'Aktualisieren' : 'Erstellen'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShopProductsTab;
